import { countries } from './countries';
import { playerNames } from './playerNames';
import type { AlbumPage, CountryAlbumPage, CountryStickerGroup, Sticker } from '../types';

const specialCodes = ['00'].concat(
  Array.from({ length: 19 }, (_, index) => `FW${index + 1}`),
);

const getStickerKind = (slotNumber: number): Sticker['kind'] => {
  if (slotNumber === 1) {
    return 'shield';
  }

  if (slotNumber === 2) {
    return 'goalkeeper';
  }

  if (slotNumber === 13) {
    return 'team_photo';
  }

  return 'player';
};

const getStickerLabel = (countryCode: string, slotNumber: number): string => {
  if (slotNumber === 1) {
    return 'Escudo';
  }

  if (slotNumber === 13) {
    return 'Foto grupal';
  }

  const name = playerNames[`${countryCode}${slotNumber}`];
  if (name) {
    return name;
  }

  // Fallback defensivo: slot 2 sin nombre en map -> "Portero"
  if (slotNumber === 2) {
    return 'Portero';
  }

  return '';
};

const getDisplayCode = (countryCode: string, slotNumber: number) => {
  if (countryCode === 'CZE' && slotNumber === 16) {
    return 'Che16';
  }

  return `${countryCode}${slotNumber}`;
};

const createCountryPages = (code: string): CountryAlbumPage[] => [
  {
    pageInCountry: 1,
    slots: [
      { type: 'country_info', colSpan: 2 },
      { type: 'sticker', stickerId: getDisplayCode(code, 1) },
      { type: 'sticker', stickerId: getDisplayCode(code, 2) },
      { type: 'sticker', stickerId: getDisplayCode(code, 3) },
      { type: 'sticker', stickerId: getDisplayCode(code, 4) },
      { type: 'sticker', stickerId: getDisplayCode(code, 5) },
      { type: 'sticker', stickerId: getDisplayCode(code, 6) },
      { type: 'sticker', stickerId: getDisplayCode(code, 7) },
      { type: 'sticker', stickerId: getDisplayCode(code, 8) },
      { type: 'sticker', stickerId: getDisplayCode(code, 9) },
      { type: 'sticker', stickerId: getDisplayCode(code, 10) },
    ],
  },
  {
    pageInCountry: 2,
    slots: [
      { type: 'sticker', stickerId: getDisplayCode(code, 11) },
      { type: 'sticker', stickerId: getDisplayCode(code, 12) },
      { type: 'sticker', stickerId: getDisplayCode(code, 13), colSpan: 2 },
      { type: 'sticker', stickerId: getDisplayCode(code, 14) },
      { type: 'sticker', stickerId: getDisplayCode(code, 15) },
      { type: 'sticker', stickerId: getDisplayCode(code, 16) },
      { type: 'sticker', stickerId: getDisplayCode(code, 17) },
      { type: 'group_info' },
      { type: 'sticker', stickerId: getDisplayCode(code, 18) },
      { type: 'sticker', stickerId: getDisplayCode(code, 19) },
      { type: 'sticker', stickerId: getDisplayCode(code, 20) },
    ],
  },
];

const countryStickers = countries.flatMap<Sticker>((country) =>
  Array.from({ length: 20 }, (_, index) => {
    const slotNumber = index + 1;
    const displayCode = getDisplayCode(country.code, slotNumber);

    return {
      id: displayCode,
      displayCode,
      slotNumber,
      countryId: country.id,
      countryName: country.name,
      countryFlag: country.flag,
      group: country.group,
      kind: getStickerKind(slotNumber),
      playerNumber: slotNumber,
      label: getStickerLabel(country.code, slotNumber),
      rarity: slotNumber === 1 || slotNumber === 13 ? 'special' : 'normal',
    };
  }),
);

// id interno se mantiene como 'FW1...FW19' para no romper datos guardados en
// Firestore. displayCode pasa a 'FWC1...FWC19' que es el codigo real impreso
// en el album Panini (lo que el usuario ve y escribe). El OCR ya conoce el
// mapeo print FWC -> internal FW (ver scan/ocrParser.ts).
const specialStickers: Sticker[] = specialCodes.map((internalCode, index) => {
  const displayCode = internalCode.startsWith('FW') ? `FWC${internalCode.slice(2)}` : internalCode;
  return {
    id: internalCode,
    displayCode,
    slotNumber: index,
    kind: 'special',
    label: `Especial ${displayCode}`,
    rarity: 'special',
  };
});

// Subcoleccion Coca-Cola: 14 jugadores que vienen fuera de los sobres (en
// botellas de Coca-Cola). Opcional para el usuario.
const cocaColaPlayers = [
  'Lamine Yamal',       // CC1
  'Joshua Kimmich',     // CC2
  'Harry Kane',         // CC3
  'Santiago Giménez',   // CC4
  'Josko Gvardiol',     // CC5
  'Federico Valverde',  // CC6
  'Jefferson Lerma',    // CC7
  'Enner Valencia',     // CC8
  'Gabriel Magalhães',  // CC9
  'Virgil van Dijk',    // CC10
  'Alphonso Davies',    // CC11
  'Emiliano Martínez',  // CC12
  'Raúl Jiménez',       // CC13
  'Lautaro Martínez',   // CC14
];

const cocaColaStickers: Sticker[] = cocaColaPlayers.map((name, index) => ({
  id: `CC${index + 1}`,
  displayCode: `CC${index + 1}`,
  slotNumber: index + 1,
  kind: 'special',
  label: name,
  rarity: 'special',
}));

export const allStickers = specialStickers.concat(countryStickers);
export const allStickersWithCocaCola = allStickers.concat(cocaColaStickers);

export const countryStickerGroups: CountryStickerGroup[] = countries.map((country) => ({
  country,
  stickers: countryStickers.filter((sticker) => sticker.countryId === country.id),
  pages: createCountryPages(country.code),
}));

export const specialStickerGroup = {
  country: { id: 'especiales', name: 'Specials', group: 'Specials', code: 'FW' },
  stickers: specialStickers,
  pages: [] as CountryAlbumPage[],
};

export const cocaColaStickerGroup = {
  country: { id: 'cocacola', name: 'Coca-Cola', group: 'Coca-Cola', code: 'CC' },
  stickers: cocaColaStickers,
  pages: [] as CountryAlbumPage[],
};

export const albumPages: AlbumPage[] = [];

export const formatStickerNumber = (displayCode: string) => displayCode;
